import {useEffect, useState} from 'react';

import AntibodyAnalysisStore, {MatchMetadata} from 'molbio/antibody-engineering/antibodyAnalysis';
import {capitalize} from 'util/NameUtil';

import {CDRDefinition, CHAIN_DISPLAY_NAMES, NumberingScheme} from './AntibodyEngineeringConstants';

export function getMetadataSummary(metadata: MatchMetadata): string {
  if (metadata.organism && metadata.chainType) {
    return `${capitalize(metadata.organism)} ${CHAIN_DISPLAY_NAMES[metadata.chainType]}`;
  }

  return '';
}

async function fetchAndSetData(
  residues: string,
  numberingScheme: NumberingScheme | undefined,
  cdrDefinition: CDRDefinition | undefined,
  setMessage: (s: string | undefined) => void,
  setOrganismInfo: (s: string | undefined) => void
): Promise<void> {
  let cdrMessage;
  if (cdrDefinition) {
    await AntibodyAnalysisStore.getCDRAnnotations([residues], cdrDefinition);
    cdrMessage = AntibodyAnalysisStore.getCachedCDRMessage(residues, cdrDefinition);
  }
  let numberingMessage;
  if (numberingScheme) {
    await AntibodyAnalysisStore.getNumbering([residues], numberingScheme);
    numberingMessage = AntibodyAnalysisStore.getCachedNumberingMessage(residues, numberingScheme);
  }
  setMessage(numberingMessage || cdrMessage);

  const metadatas = AntibodyAnalysisStore.getCachedMetadata(residues);
  let summary;
  if (metadatas) {
    summary = metadatas.map((m) => getMetadataSummary(m)).join(' | ');
  }
  setOrganismInfo(summary);
}

export default function useAntibodyMetadata(
  residues: string | null,
  numberingScheme: NumberingScheme | undefined,
  cdrDefinition: CDRDefinition | undefined
): [string | undefined, string | undefined] {
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [organismInfo, setOrganismInfo] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (residues) {
      fetchAndSetData(residues, numberingScheme, cdrDefinition, setMessage, setOrganismInfo);
    }
  }, [residues, numberingScheme, cdrDefinition]);

  return [message, organismInfo];
}
